import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { PhoneCall, Check, MapPin, ArrowRight, Star, Wifi, Shield, Zap } from 'lucide-react';

const providers = [
  { name: 'Telekom', color: 'bg-pink-50 border-pink-200', badge: 'Beste Netzqualität' },
  { name: 'Vodafone', color: 'bg-red-50 border-red-200', badge: 'Große Auswahl' },
  { name: 'O2', color: 'bg-blue-50 border-blue-200', badge: 'Günstige Preise' },
  { name: '', color: 'bg-orange-50 border-orange-200', badge: 'Viele Inklusivleistungen' },
];

const benefits = [
  { icon: <MapPin className="w-5 h-5 text-blue-600" />, title: 'Lokal im Saarland', text: 'Persönliche Beratung für Saarbrücken und Umgebung – wir kennen die Netzversorgung vor Ort.' },
  { icon: <Shield className="w-5 h-5 text-blue-600" />, title: 'Alle Anbieter im Vergleich', text: 'Telekom, Vodafone, O2 – wir finden den besten Tarif für Sie.' },
  { icon: <Star className="w-5 h-5 text-blue-600" />, title: 'Kostenlose Beratung', text: 'Unverbindlich und ohne versteckte Kosten – wir verdienen nur bei Abschluss.' },
  { icon: <Zap className="w-5 h-5 text-blue-600" />, title: 'Schnelle Abwicklung', text: 'Vom Vergleich bis zum Vertragsabschluss – alles aus einer Hand.' },
];

const faqs = [
  {
    q: 'Welcher Handyvertrag ist in Saarbrücken am günstigsten?',
    a: 'Das hängt von Ihrem Datenvolumen und Netzwunsch ab. In Saarbrücken haben alle großen Netze gute Abdeckung. Wir vergleichen alle Anbieter kostenlos für Sie.',
  },
  {
    q: 'Kann ich in Saarbrücken ein Smartphone mit Vertrag kaufen?',
    a: 'Ja! Wir bieten Smartphones (iPhone, Samsung, Google Pixel u.v.m.) in Kombination mit Handyverträgen aller großen Anbieter an.',
  },
  {
    q: 'Wie gut ist die 5G-Abdeckung in Saarbrücken?',
    a: 'Saarbrücken hat eine sehr gute 5G-Abdeckung bei Telekom, Vodafone und O2. Prüfen Sie, ob Ihr Tarif 5G inklusive hat.',
  },
  {
    q: 'Wo kann ich mich in Saarbrücken beraten lassen?',
    a: 'Wir beraten Sie persönlich in unserer Filiale in Völklingen (nur 15 Minuten von Saarbrücken entfernt) oder online per WhatsApp.',
  },
];

const HandyvertragSaarbrueckenPage: React.FC = () => {
  return (
    <div className="pt-24 pb-16">
      <Helmet>
        <title>Handyvertrag Saarbrücken - Alle Anbieter vergleichen | Easy Vertrag</title>
        <meta name="description" content="Handyvertrag in Saarbrücken abschließen - persönliche Beratung im Saarland. Telekom, Vodafone, O2 vergleichen. Kostenlos beraten lassen!" />
        <meta name="keywords" content="Handyvertrag Saarbrücken, Mobilfunkvertrag Saarbrücken, Handytarif Saarbrücken, Telekom Saarbrücken, Vodafone Saarbrücken, O2 Saarbrücken" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://easy4vertrag.de/handyvertrag-saarbruecken" />
        <meta property="og:title" content="Handyvertrag Saarbrücken - Alle Anbieter vergleichen | Easy Vertrag" />
        <meta property="og:description" content="Handyvertrag in Saarbrücken abschließen - persönliche Beratung. Telekom, Vodafone, O2 kostenlos vergleichen." />
        <meta property="og:url" content="https://easy4vertrag.de/handyvertrag-saarbruecken" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              ${faqs.map(f => `{
                "@type": "Question",
                "name": "${f.q}",
                "acceptedAnswer": {"@type": "Answer", "text": "${f.a}"}
              }`).join(',')}
            ]
          }
        `}</script>
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://easy4vertrag.de/"},
              {"@type": "ListItem", "position": 2, "name": "Handyvertrag Saarbrücken", "item": "https://easy4vertrag.de/handyvertrag-saarbruecken"}
            ]
          }
        `}</script>
      </Helmet>

      <section className="bg-gradient-to-br from-blue-700 to-blue-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white bg-opacity-15 rounded-full px-4 py-2 text-sm font-medium mb-6">
              <MapPin className="w-4 h-4" />
              Saarbrücken · Saarland
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold mb-5 leading-tight">
              Handyvertrag in Saarbrücken abschließen
            </h1>
            <p className="text-xl text-blue-100 mb-4">
              Ihr lokaler Experte für Mobilfunkverträge im Saarland. Telekom, Vodafone, O2 kostenlos vergleichen.
            </p>
            <p className="text-blue-200 mb-8 text-base">
              Beratung in Völklingen (15 Min. von Saarbrücken) · +49 179 7473491
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/angebote/tarif" className="inline-flex items-center justify-center px-7 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors">
                Tarife jetzt vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <a href="https://wa.me/491797473491" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-blue-700 transition-colors">
                WhatsApp Beratung
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">
              Warum Handyvertrag in Saarbrücken bei Easy Vertrag?
            </h2>
            <p className="text-gray-500 text-center mb-10 max-w-2xl mx-auto">
              Als lokaler Anbieter im Saarland kennen wir die Netzversorgung in Saarbrücken genau und finden den Tarif, der wirklich zu Ihnen passt.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {benefits.map((b, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-gray-50 rounded-xl border border-gray-100">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">{b.icon}</div>
                  <div>
                    <h3 className="font-bold text-gray-800 mb-1">{b.title}</h3>
                    <p className="text-gray-600 text-sm">{b.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-3">
              Handyverträge aller Anbieter für Saarbrücken
            </h2>
            <p className="text-gray-500 text-center mb-10">
              Wir vermitteln Verträge von allen großen Netzbetreibern – neutral und unabhängig.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
              {providers.map((p, i) => (
                <div key={i} className={`border-2 rounded-xl p-5 text-center ${p.color}`}>
                  <p className="text-xl font-bold text-gray-800 mb-2">{p.name}</p>
                  <span className="text-xs font-medium text-gray-500 bg-white px-2 py-1 rounded-full border">{p.badge}</span>
                </div>
              ))}
            </div>
            <div className="bg-blue-600 text-white rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold mb-3">Kostenloser Tarifvergleich für Saarbrücken</h3>
              <p className="text-blue-100 mb-6 max-w-lg mx-auto">
                Unser Rechner zeigt Ihnen alle verfügbaren Tarife mit dem besten Preis-Leistungs-Verhältnis.
              </p>
              <Link to="/angebote/tarif" className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-bold rounded-lg hover:bg-blue-50 transition-colors">
                Jetzt Tarife vergleichen <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-10">
              Häufige Fragen zu Handyverträgen in Saarbrücken
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <h3 className="font-bold text-gray-800 mb-2 text-base">{faq.q}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 bg-blue-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Jetzt Handyvertrag in Saarbrücken abschließen</h2>
            <p className="text-blue-100 mb-2">Easy Vertrag · Bismarck Straße 31 · 66333 Völklingen (15 Min. von Saarbrücken)</p>
            <p className="text-blue-200 text-sm mb-8">Mo–Fr 10:00–20:00 Uhr · Sa 10:00–18:00 Uhr</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:+491797473491" className="inline-flex items-center justify-center px-7 py-3 bg-white text-blue-700 font-semibold rounded-lg hover:bg-blue-50 transition-colors">
                <PhoneCall className="w-5 h-5 mr-2" /> Jetzt anrufen
              </a>
              <Link to="/kontakt" className="inline-flex items-center justify-center px-7 py-3 border-2 border-white bg-transparent text-white font-semibold rounded-lg hover:bg-white hover:text-blue-700 transition-colors">
                Nachricht schicken <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HandyvertragSaarbrueckenPage;
